import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener('change', this.#handleVariantPickerChange, { signal });
    this.addEventListener('wheel', this.#handleGalleryWheel, { passive: false, signal });

    this.#applyColorMediaFilter();
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  #lastWheelSlideAt = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  #handleVariantPickerChange = (event) => {
    if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement)) return;
    if (!event.target.closest('variant-picker')) return;

    requestAnimationFrame(() => this.#applyColorMediaFilter());
  };

  #handleGalleryWheel = (event) => {
    if (!this.closest('.product-center-gallery')) return;
    if (!window.matchMedia('(min-width: 990px)').matches) return;
    if (Math.abs(event.deltaY) < Math.abs(event.deltaX)) return;

    const slideshow = this.slideshow;
    const slides = slideshow?.slides;

    if (!slideshow || !slides || slides.length <= 1) return;

    const direction = Math.sign(event.deltaY);
    const nextIndex = slideshow.current + direction;
    const isAtStart = direction < 0 && slideshow.current <= 0;
    const isAtEnd = direction > 0 && slideshow.current >= slides.length - 1;

    if (!direction || isAtStart || isAtEnd) return;

    event.preventDefault();

    const now = Date.now();
    if (now - this.#lastWheelSlideAt < 520) return;

    this.#lastWheelSlideAt = now;
    slideshow.select(nextIndex, event, { animate: true });
  }

  #applyColorMediaFilter() {
    const selectedColor = this.#getSelectedColor();

    if (!selectedColor) return;

    const filterableItems = [
      ...this.querySelectorAll('slideshow-slide'),
      ...this.querySelectorAll('.media-gallery__grid > .product-media-container'),
      ...this.querySelectorAll('.slideshow-controls__thumbnail'),
      ...this.querySelectorAll('.dialog-thumbnails-list__thumbnail'),
      ...this.querySelectorAll('.dialog-zoomed-gallery > .product-media-container'),
    ];

    let hasTaggedMedia = false;

    for (const item of filterableItems) {
      const image = item.querySelector('img[alt]');
      const mediaColor = this.#getColorFromAlt(image?.getAttribute('alt') || '');

      if (mediaColor) hasTaggedMedia = true;
    }

    if (!hasTaggedMedia) return;

    for (const item of filterableItems) {
      const image = item.querySelector('img[alt]');
      const mediaColor = this.#getColorFromAlt(image?.getAttribute('alt') || '');

      item.hidden = mediaColor !== selectedColor;
    }

    this.#syncFilteredThumbnailIndexes();

    const slides = Array.from(this.querySelectorAll('slideshow-slide'));
    const firstVisibleSlideIndex = slides.findIndex((slide) => !slide.hidden);

    if (firstVisibleSlideIndex >= 0) {
      const firstVisibleSlide = slides[firstVisibleSlideIndex];
      const firstVisibleSlideId = firstVisibleSlide?.getAttribute('slide-id');

      if (firstVisibleSlideId) {
        this.slideshow?.select({ id: firstVisibleSlideId }, undefined, { animate: false });
      }
    }
  }

  #syncFilteredThumbnailIndexes() {
    const thumbnails = Array.from(this.querySelectorAll('.slideshow-controls__thumbnail'));
    let visibleIndex = 0;

    for (const thumbnail of thumbnails) {
      if (thumbnail.hasAttribute('hidden')) continue;

      thumbnail.setAttribute('on:click', `/select/${visibleIndex}`);
      visibleIndex += 1;
    }
  }

  #getSelectedColor() {
    const section = this.closest('.shopify-section');
    const variantPicker = section?.querySelector('variant-picker');

    if (!variantPicker) return '';

    for (const fieldset of variantPicker.querySelectorAll('fieldset')) {
      const legend = fieldset.querySelector('legend');
      const legendLabel = legend?.cloneNode(true);

      if (legendLabel instanceof HTMLElement) {
        legendLabel.querySelector('.variant-option__swatch-value')?.remove();
      }

      if (this.#normalizeColorValue(legendLabel?.textContent || '') !== 'color') continue;

      const checkedInput = fieldset.querySelector('input:checked');

      return this.#normalizeColorValue(checkedInput?.value || '');
    }

    for (const singleOption of variantPicker.querySelectorAll('[data-testid="variant-option-single"]')) {
      const selectedValue = singleOption.querySelector('[data-testid="variant-option-single-value"]');
      const optionLabel = singleOption.cloneNode(true);

      if (optionLabel instanceof HTMLElement) {
        optionLabel.querySelector('[data-testid="variant-option-single-value"]')?.remove();
      }

      if (this.#normalizeColorValue(optionLabel.textContent || '') !== 'color') continue;

      return this.#normalizeColorValue(selectedValue?.textContent || '');
    }

    for (const select of variantPicker.querySelectorAll('select')) {
      const label = variantPicker.querySelector(`label[for="${select.id}"]`);

      if (this.#normalizeColorValue(label?.textContent || '') !== 'color') continue;

      return this.#normalizeColorValue(select.value);
    }

    return '';
  }

  #getColorFromAlt(alt) {
    const match = alt.match(/(?:^|\s)color:\s*([^|,;]+)/i);

    return this.#normalizeColorValue(match?.[1] || '');
  }

  #normalizeColorValue(value) {
    const normalizedValue = value
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    const colorAliases = {
      negro: 'black',
      gris: 'grey',
    };

    return colorAliases[normalizedValue] || normalizedValue;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
